import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, Pencil, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { INSIGHT_CATEGORIES } from "@/data/site";
import { GovXLogo } from "@/components/GovXLogo";

const CATS = INSIGHT_CATEGORIES.filter((c) => c !== "All");
const CONTENT_TYPES = ["GovX Analysis", "Policy Brief", "Governance Explainer", "State Tracker", "Programme Review", "Data Story", "Opinion", "Report"];

const emptyInsight = { title: "", category: "Governance", content_type: "GovX Analysis", excerpt: "", body: "", image_url: "", author: "GovX Intelligence", published: true };
const emptyReport = { title: "", description: "", category: "Governance", image_url: "", status: "Coming Soon", year: "2026" };

const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-govx-grey">{label}</span>
    <input {...props} className="mt-1.5 w-full border-b border-white/15 bg-transparent py-2 text-sm text-govx-paper outline-none focus:border-govx-lime" />
  </label>
);
const Select = ({ label, options, ...props }) => (
  <label className="block">
    <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-govx-grey">{label}</span>
    <select {...props} className="mt-1.5 w-full border-b border-white/15 bg-govx-charcoal py-2 text-sm text-govx-paper outline-none focus:border-govx-lime">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("insights");
  const [insights, setInsights] = useState([]);
  const [reports, setReports] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // {type, data}

  const load = useCallback(() => {
    api.get("/admin/insights").then((r) => setInsights(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === false) nav("/govx-admin/login");
    if (user && user.role === "admin") load();
  }, [user, nav, load]);

  if (!user || user.role !== "admin")
    return <div className="flex min-h-screen items-center justify-center bg-govx-black font-mono text-sm text-govx-grey">Checking access…</div>;

  const save = async () => {
    const { type, data } = editing;
    const base = type === "insight" ? "/admin/insights" : "/admin/reports";
    try {
      if (data.id) await api.put(`${base}/${data.id}`, data);
      else await api.post(base, data);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save.");
    }
  };

  const remove = async (type, id) => {
    const base = type === "insight" ? "/admin/insights" : "/admin/reports";
    try {
      await api.delete(`${base}/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Could not delete.");
    }
  };

  const upd = (k, v) => setEditing((e) => ({ ...e, data: { ...e.data, [k]: v } }));

  return (
    <div className="min-h-screen bg-govx-black text-govx-paper grain">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-govx-black/90 px-6 py-4 backdrop-blur">
        <GovXLogo variant="light" showSub={false} />
        <div className="flex items-center gap-4">
          <span className="hidden font-mono text-xs text-govx-grey sm:block">{user.email}</span>
          <button onClick={() => { logout(); nav("/govx-admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold hover:border-govx-lime hover:text-govx-lime">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>

      <div className="relative z-10 mx-auto max-w-6xl px-6 py-10">
        <div className="flex gap-2">
          {[["insights", "Insights"], ["reports", "Reports"], ["contacts", `Enquiries (${contacts.length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-govx-green text-white" : "border border-white/15 text-govx-grey hover:text-govx-paper"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* INSIGHTS */}
        {tab === "insights" && (
          <div className="mt-8">
            <button onClick={() => setEditing({ type: "insight", data: { ...emptyInsight } })} data-testid="new-insight" className="inline-flex items-center gap-2 rounded-full bg-govx-lime px-5 py-2.5 text-sm font-semibold text-govx-black">
              <Plus className="h-4 w-4" /> New Insight
            </button>
            <div className="mt-6 divide-y divide-white/8 border border-white/10">
              {insights.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <div className="font-display font-medium">{it.title}</div>
                    <div className="font-mono text-[0.62rem] uppercase tracking-wider text-govx-grey">{it.category} · {it.content_type}</div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button onClick={() => setEditing({ type: "insight", data: { ...emptyInsight, ...it } })} className="rounded-full border border-white/15 p-2 hover:text-govx-lime"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove("insight", it.id)} data-testid={`del-insight-${it.id}`} className="rounded-full border border-white/15 p-2 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* REPORTS */}
        {tab === "reports" && (
          <div className="mt-8">
            <button onClick={() => setEditing({ type: "report", data: { ...emptyReport } })} data-testid="new-report" className="inline-flex items-center gap-2 rounded-full bg-govx-lime px-5 py-2.5 text-sm font-semibold text-govx-black">
              <Plus className="h-4 w-4" /> New Report
            </button>
            <div className="mt-6 divide-y divide-white/8 border border-white/10">
              {reports.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <div className="font-display font-medium">{it.title}</div>
                    <div className="font-mono text-[0.62rem] uppercase tracking-wider text-govx-grey">{it.category} · {it.status}</div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button onClick={() => setEditing({ type: "report", data: { ...emptyReport, ...it } })} className="rounded-full border border-white/15 p-2 hover:text-govx-lime"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove("report", it.id)} className="rounded-full border border-white/15 p-2 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTACTS */}
        {tab === "contacts" && (
          <div className="mt-8 space-y-3">
            {contacts.length === 0 && <p className="font-mono text-sm text-govx-grey">No enquiries yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="border border-white/10 p-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[0.62rem] uppercase tracking-wider text-govx-lime">
                  <span>{Array.isArray(c.contact_as) ? c.contact_as.join(", ") : c.contact_as}</span><span className="text-govx-grey">/ {Array.isArray(c.need_help_with) ? c.need_help_with.join(", ") : c.need_help_with}</span>
                </div>
                <div className="mt-2 font-display text-lg font-medium">{c.name} <span className="text-govx-grey">· {c.organisation}</span></div>
                <div className="mt-1 font-mono text-xs text-govx-grey">{c.email} · {c.phone}</div>
                <p className="mt-3 text-sm text-govx-paper/80">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-white/12 bg-govx-charcoal p-7" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-medium">{editing.data.id ? "Edit" : "New"} {editing.type}</h3>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-govx-grey" /></button>
            </div>
            <div className="mt-6 space-y-5">
              <Input label="Title" value={editing.data.title} onChange={(e) => upd("title", e.target.value)} />
              {editing.type === "insight" ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Select label="Category" options={CATS} value={editing.data.category} onChange={(e) => upd("category", e.target.value)} />
                    <Select label="Content Type" options={CONTENT_TYPES} value={editing.data.content_type} onChange={(e) => upd("content_type", e.target.value)} />
                  </div>
                  <Input label="Author" value={editing.data.author} onChange={(e) => upd("author", e.target.value)} />
                  <Input label="Image URL" value={editing.data.image_url} onChange={(e) => upd("image_url", e.target.value)} />
                  <label className="block">
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-govx-grey">Excerpt</span>
                    <textarea rows={2} value={editing.data.excerpt} onChange={(e) => upd("excerpt", e.target.value)} className="mt-1.5 w-full resize-none border-b border-white/15 bg-transparent py-2 text-sm text-govx-paper outline-none focus:border-govx-lime" />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-govx-grey">Body</span>
                    <textarea rows={5} value={editing.data.body} onChange={(e) => upd("body", e.target.value)} className="mt-1.5 w-full resize-none border-b border-white/15 bg-transparent py-2 text-sm text-govx-paper outline-none focus:border-govx-lime" />
                  </label>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Select label="Category" options={CATS} value={editing.data.category} onChange={(e) => upd("category", e.target.value)} />
                    <Select label="Status" options={["Coming Soon", "Available"]} value={editing.data.status} onChange={(e) => upd("status", e.target.value)} />
                  </div>
                  <Input label="Year" value={editing.data.year} onChange={(e) => upd("year", e.target.value)} />
                  <Input label="Image URL" value={editing.data.image_url} onChange={(e) => upd("image_url", e.target.value)} />
                  <label className="block">
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-govx-grey">Description</span>
                    <textarea rows={3} value={editing.data.description} onChange={(e) => upd("description", e.target.value)} className="mt-1.5 w-full resize-none border-b border-white/15 bg-transparent py-2 text-sm text-govx-paper outline-none focus:border-govx-lime" />
                  </label>
                </>
              )}
            </div>
            <button onClick={save} data-testid="save-item" className="mt-7 w-full rounded-full bg-govx-green py-3 text-sm font-semibold text-white hover:bg-govx-green-deep">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
